/*
Reusable hook that provides endpoints/requests to convert, save and create emails via the backend graph service
*/
import _ from "lodash";
import { FormState } from "react-hook-form";
import { useCreateDraftMailMutation, useCreateForwardDraftMailMutation, useCreateReplyDraftMailMutation, useSendDraftMailMutation } from "../services/useremails";

export type SaveDraftResponse = Partial<{ id: string }>;
export type SendMailResponse = Partial<{ internetMessageId: string, graphId: string, sentDate: string | null }>;

export type MailMessageFormControls = {
    convertToForm: (form: MailMessage) => FormData
    isBusy: boolean
    isDirty: (formState: FormState<MailMessage>) => boolean
    isRestricted: (form: MailMessage) => boolean
    saveDraft: (form: MailMessage) => Promise<SaveDraftResponse>
    sendMail: (mailId: string, senderId: string, masterAccountId: string | null | undefined, forceFile: boolean | null) => Promise<SendMailResponse>
};

export const useMailMessageForm = (mailType: MailType) : MailMessageFormControls => {
    const [createDraftMailTrigger, { isLoading: isCreatingDraft }] = useCreateDraftMailMutation();
    const [createReplyMailTrigger, { isLoading: isCreatingReply }] = useCreateReplyDraftMailMutation();
    const [createForwardMailTrigger, { isLoading: isCreatingForward }] = useCreateForwardDraftMailMutation();
    const [sendDraftEmail, { isLoading: isSending }] = useSendDraftMailMutation();
    const isBusy = isCreatingDraft || isCreatingReply || isCreatingForward || isSending;

    const convertToForm = (form: MailMessage) : FormData => {
        switch (mailType) {
            case 'new': 
                return convertToFormData(form);
            case 'forward': 
                let forwardFormData = convertToFormData(form);
                forwardFormData.delete('body');
                forwardFormData.append('comment', form.body ?? '');
                return forwardFormData;
            case 'reply': 
            case 'reply-all':
                let replyFormData = convertToFormData(form);
                replyFormData.delete('body');
                replyFormData.append('comment', form.body ?? '');
                replyFormData.append('isReplyAll', mailType === 'reply-all' ? 'true' : 'false');
                return replyFormData;
        }
    };

    const convertToFormData = (form: MailMessage) : FormData => {
        const newFormData = new FormData();
        const { from, subject, body, masterAccountId, projectId, bccRecipients, ccRecipients, toRecipients, attachments } = form;

        // TODO: do we need to have "document" attachments?
        const dataAttachments = attachments ?? [];
        dataAttachments
            .filter((el: MailMessageAttachment) => el.file)
            .map((el: MailMessageAttachment) => el.file!)
            .forEach((file, index) => newFormData.append("file" + index, file, file.name));

        
        if (masterAccountId && masterAccountId !== null) {
            newFormData.append('masterAccountId', masterAccountId);
        }

        if (projectId && projectId !== null) {
            newFormData.append('projectId', projectId);
        }

        newFormData.append("from", from);
        newFormData.append("subject", subject ?? '');
        newFormData.append("body", body ?? '');

        if (toRecipients && toRecipients !== null && toRecipients.length !== 0) {
            newFormData.append("toRecipientsJson", JSON.stringify(toRecipients));
        }

        if (ccRecipients && ccRecipients !== null && ccRecipients.length !== 0) {
            newFormData.append("ccRecipientsJson", JSON.stringify(ccRecipients));
        }

        if (bccRecipients && bccRecipients !== null && bccRecipients.length !== 0) {
            newFormData.append("bccRecipientsJson", JSON.stringify(bccRecipients));
        }

        return newFormData;
    };

    const handleIsDirty = (formState: FormState<MailMessage>) : boolean => {
        const dirtyFields = Object.keys(formState.dirtyFields).map((k) => ({
            key: k,
            isDirty: formState.dirtyFields[k]
        }));
        return _.some(dirtyFields, 'isDirty');
    };

    const handleIsRestricted = (form: MailMessage) : boolean => {
        const { toRecipients, ccRecipients, bccRecipients } = form;
        const ccCheck = ccRecipients ?? [];
        const bccCheck = bccRecipients ?? [];

        return toRecipients.some(x => x.isRestricted) 
            || (ccCheck.some(x => x.isRestricted))
            || (bccCheck.some(x => x.isRestricted));
    };

    const handleSaveDraft = (form: MailMessage) : Promise<SaveDraftResponse> => new Promise((resolve, reject) => {
        const formData = convertToForm(form);
        switch(mailType) {
            case 'new': 
                return createDraftMailTrigger({ userId: form.from, request: formData }).unwrap().then(resolve, reject);
            case 'forward':
                return createForwardMailTrigger({ userId: form.from, id: form.parentMailMessageId, request: formData }).unwrap().then(resolve, reject);
            case 'reply':
            case 'reply-all':
                return createReplyMailTrigger({ userId: form.from, id: form.parentMailMessageId, request: formData }).unwrap().then(resolve, reject);
        }
    });

    const handleSendMail = (mailId: string, senderId: string, masterAccountId: string | null | undefined, forceFile: boolean | null) : Promise<SendMailResponse> => new Promise((resolve, reject) => {
        return sendDraftEmail({ userId: senderId, id: mailId, masterAccountId, forceFile }).unwrap().then(resolve, reject);
    });

    return { 
        convertToForm,
        isBusy,
        isDirty: handleIsDirty,
        isRestricted: handleIsRestricted,
        saveDraft: handleSaveDraft,
        sendMail: handleSendMail
    };
};